import type {
    PlayerStateController,
    ReplayPlayerState,
} from "./playerstate/PlayerStateController.js";
import type { DiceController } from "./dice/DiceController.js";
import type { BoardController, ReplayMove } from "./board/BoardController.js";
import type { GameSource } from "./source/GameSource.js";
import type { Game } from "../../../royalur/Game.js";
import type { ReactionController } from "./reactions/ReactionController.js";
import { GameController } from "./GameController.js";
import { GameDirective } from "./GameDirective.js";
import type { BoardDirective } from "./board/BoardDirective.js";
import type { DiceDirective } from "./dice/DiceDirective.js";
import type { PlayerStateDirective } from "./playerstate/PlayerStateDirective.js";
import type { GameThemeType } from "../theme/GameThemeType.js";
import type { ReplayMoveProperties } from "./board/ReplayMovesBoardDirective.js";
import { clamp } from "../../../util/numbers.js";
import { MoveInfo } from "../../analysis/MoveInfo.js";
import { PlayerType } from "../../../royalur/model/PlayerType.js";
import type { GameState } from "../../../royalur/engine/state/GameState.js";


/**
 * Plays back perfect games.
 */
export class ReplayGameController extends GameController {

    private game: Game;
    private moves: MoveInfo[] = [];

    constructor(
        playerStateController: PlayerStateController,
        reactionController: ReactionController,
        diceController: DiceController,
        boardController: BoardController,
        source: GameSource,
        defaultThemeType: GameThemeType,
    ) {
        super(
            playerStateController,
            reactionController,
            diceController,
            boardController,
            source,
            defaultThemeType,
        );

        this.game = source.game.get();
        this.init(this.game);

        this.postConstructor();
    }

    override setup(): () => void {
        const cleanup: (() => void)[] = [
            super.setup(),
        ];
        cleanup.push(this.source.game.subscribe(
            game => this.init(game),
        ));
        return () => cleanup.forEach(fn => fn());
    }

    private init(game: Game) {
        this.game = game;
        this.moves = MoveInfo.listMoves(this.game);
        this.update();
    }

    skip() {
        // We want to start our new directives immediately.
        this.boardController.clearNonLimboDirectives();
        this.diceController.clearNonLimboDirectives();
        this.playerStateController.clearNonLimboDirectives();
    }

    generateGameDirective(
        moveIndex: number,
        state: GameState,
        moves: ReplayMove[],
        playerStates: ReplayPlayerState[],
        moveProperties?: ReplayMoveProperties[],
    ): GameDirective {
        const subject = state.getSubject() ?? PlayerType.LIGHT;

        const boardDirectives: BoardDirective[] = [];
        const diceDirectives: DiceDirective[] = [];
        const playerStateDirectives: PlayerStateDirective[] = [];

        // Create the dice directive.
        diceDirectives.push(...this.diceController.createHidden());

        // Create the board directive.
        if (moves.length > 0) {
            boardDirectives.push(...this.boardController.createReplayMoves(
                this.source.getRules(), moves, moveProperties,
            ));
        } else {
            boardDirectives.push(...this.boardController.createWait(
                this.source.getRules(),
                moveIndex,
                state,
                subject,
                state.isFinished(),
            ));
        }

        // Create the player state directive.
        if (playerStates.length > 0) {
            playerStateDirectives.push(...this.playerStateController.createReplay(
                playerStates,
            ));
        } else {
            playerStateDirectives.push(...this.playerStateController.createBasic(
                moveIndex,
                state.getLightPlayer(),
                state.getDarkPlayer(),
                subject,
            ));
        }

        // Create the directive!
        return new GameDirective(
            this.assignNextDirectiveID(),
            state,
            boardDirectives,
            diceDirectives,
            playerStateDirectives,
        );
    }

    static getMoveLWP(move: MoveInfo): number | undefined {
        if (!move.moveState)
            return undefined;

        const lwpStr = move.moveState.getMetadata()["lwp"];
        if (lwpStr === undefined)
            return undefined;

        return parseFloat(lwpStr);
    }

    /**
     * Calculates speed and wait times for moves, to fast-forward
     * boring moments and to slow-down dramatic ones.
     */
    static calculateMoveProperties(
        moves: ReplayMove[],
        moveLWPs: (number | undefined)[],
        slowMoLastMove?: boolean,
        speed?: number,
        waitSpeed?: number,
    ): ReplayMoveProperties[] {

        const moveProperties: ReplayMoveProperties[] = [];
        const effectiveSpeed = (speed === undefined ? 0.85 : speed);
        const effectiveWaitSpeed = effectiveSpeed * (waitSpeed === undefined ? 1 : waitSpeed);
        for (let index = 0; index < moves.length; ++index) {
            const move = moves[index];
            const lastLWP = moveLWPs[index - 2];
            const lwp = moveLWPs[index - 1];
            const nextLWP = moveLWPs[index];

            let wait = 0.0;
            let speed = 1.0;
            if (!slowMoLastMove || index < moves.length - 1) {
                if (lastLWP !== undefined && lwp !== undefined) {
                    const diff = Math.abs(lwp - lastLWP);
                    wait = clamp(
                        -0.2 + 0.075 * diff,
                        0, 0.2,
                    );
                }
                if (lwp !== undefined && nextLWP !== undefined) {
                    const diff = Math.abs(nextLWP - lwp);
                    speed = clamp(
                        0.8 + 0.075 * diff,
                        1.0, 1.15,
                    );
                }
                // We don't want to speed up scores.
                if (move.move.isScore()) {
                    if (effectiveWaitSpeed > 0) {
                        wait /= effectiveWaitSpeed;
                    }
                    if (effectiveSpeed > 0) {
                        speed /= effectiveSpeed;
                    }
                }
            } else {
                wait = 0.2;
                speed = 2.0;
            }

            moveProperties.push({
                wait: effectiveWaitSpeed * wait,
                speed: effectiveSpeed * speed,
            });
        }
        return moveProperties;
    }

    update() {
        const moves: ReplayMove[] = [];
        const moveLWPs: (number | undefined)[] = [];
        const playerStates: ReplayPlayerState[] = [];

        let lastLWP: number | undefined = undefined;
        for (const move of this.moves) {
            lastLWP = ReplayGameController.getMoveLWP(move);
            if (lastLWP !== null)
                break;
        }

        for (let moveIndex = 0; moveIndex < this.moves.length; ++moveIndex) {
            const move = this.moves[moveIndex];
            const lwp: number | undefined = ReplayGameController.getMoveLWP(move) ?? lastLWP;
            lastLWP = lwp;
            if (move.moveState) {
                moves.push({
                    moveIndex,
                    state: move.moveState,
                    move: move.moveState.getMove(),
                });
                moveLWPs.push(lwp);

                // Wait until after the move to update the score.
                const theMove = move.moveState.getMove();
                if ((theMove.isScore() || theMove.isCapture()) && move.waitingForMoveState) {
                    playerStates.push({
                        moveIndex,
                        lightPlayerState: move.waitingForMoveState.getLightPlayer(),
                        darkPlayerState: move.waitingForMoveState.getDarkPlayer(),
                        activePlayer: move.waitingForMoveState.getTurn(),
                    });
                } else {
                    playerStates.push({
                        moveIndex,
                        lightPlayerState: move.afterState.getLightPlayer(),
                        darkPlayerState: move.afterState.getDarkPlayer(),
                        activePlayer: move.moveState.getTurn(),
                    });
                }
            }
        }

        const moveProperties = ReplayGameController.calculateMoveProperties(moves, moveLWPs, true);

        // We want to start our new directives immediately.
        this.boardController.clearNonLimboDirectives();
        this.diceController.clearNonLimboDirectives();
        this.playerStateController.clearNonLimboDirectives();

        const states = this.game.getStates();
        const firstState = states[0];
        const lastState = states[states.length - 1];
        if (firstState === undefined || lastState === undefined)
            return;

        this.pushDirective(this.generateGameDirective(
            0, firstState, moves, playerStates, moveProperties,
        ));
        this.pushDirective(this.generateGameDirective(
            this.moves.length - 1, lastState, [], [], [],
        ));
    }
}
